import React, { useCallback, useMemo } from 'react';

import { CSSProp, CSSProperties } from 'styled-components';

import { Box } from '@adsk/alloy-react-box';
import Checkbox, { CheckboxState } from '@adsk/alloy-react-checkbox';
import Divider from '@adsk/alloy-react-divider';
import {
  TableConfigurationColumns,
  TableConfigurationMenu,
  TableConfigurationReset,
  TableExpanderButton,
} from '@adsk/alloy-react-table-v2';
import { STICKY_TYPES } from '@adsk/alloy-react-table-v2/es/consts';
import { TableConfigurationMenuProps } from '@adsk/alloy-react-table-v2/es/TableConfiguration';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { ActivityNameCell } from './ActivityNameCell';
import { dateFormatter, formatFloatProgress } from './formatter';
import { formatUniqueId } from '../../../helpers';
import { useTranslation } from '../../i18n';
import { ActivityCode, ActivityRow } from '../../types';
import { useScheduleContext } from '../ScheduleProvider';
import { ACTIONS } from '../ScheduleProvider/ScheduleProvider';

const useActivityCodeColumns = (activityCodes: string[]) => {
  return activityCodes.map((name: string) => ({
    id: name,
    label: name,
    renderHeader: () => <OverflowTooltip>{name}</OverflowTooltip>,
    width: 70,
    show: false,
    accessor: 'activity.activityCodes',
    renderCell: ({ value }: { value: ActivityCode[] }) => (
      <OverflowTooltip>
        {value?.find((activityCode) => activityCode.name === name)?.value ??
          '-'}
      </OverflowTooltip>
    ),
  }));
};

const DurationCell = ({ value }: { value: number | string | undefined }) => {
  const { t } = useTranslation();
  const text = typeof value === 'number' ? `${value} ${t('DAYS')}` : '-';
  return <OverflowTooltip>{text}</OverflowTooltip>;
};

export const useScheduleActivityColumns = (
  activityCodes: string[],
  toggleRowSelected: (id: string, checked: boolean) => void,
) => {
  const { t } = useTranslation();
  const activityCodeColumns = useActivityCodeColumns(activityCodes);
  const { dispatch } = useScheduleContext();

  const onExpandRow = useCallback(
    (id: string, expand: boolean) => {
      dispatch({
        type: ACTIONS.UPDATE_COLLAPSED_ROWS,
        payload: {
          key: id,
          value: !expand,
        },
      });
    },
    [dispatch],
  );

  const baseColumns = useMemo(
    () => [
      {
        id: 'checkbox',
        accessor: 'id',
        renderHeader: () => '',
        width: 32,
        maxWidth: 32,
        minWidth: 32,
        sticky: STICKY_TYPES.LEFT,
        renderCell: ({
          value: id,
          row,
        }: {
          value: string;
          row: { isSelected: boolean; original: ActivityRow };
        }) => (
          <Checkbox
            checked={row.isSelected}
            disabled={row.original.disabled}
            onChange={(state: CheckboxState) =>
              toggleRowSelected(id, state as boolean)
            }
          />
        ),
      },
      {
        id: 'id',
        width: 40,
        minWidth: 40,
        label: t('COLUMNS.ID'),
        renderHeader: () => (
          <Box
            width="100%"
            display="grid"
            gridTemplateColumns="16px 1fr"
            gridAutoFlow="column"
            marginLeft="auto"
          >
            <Box gridColumn="2">{t('COLUMNS.ID')}</Box>
          </Box>
        ),
        accessor: 'activity.sequentialId',
        renderCell: ({
          value,
          row,
        }: {
          value: number;
          row: { original: ActivityRow };
        }) => (
          <Box
            display="grid"
            gridTemplateColumns="3px 1fr"
            gridAutoFlow="column"
            alignItems="center"
            style={{
              columnGap: '13px',
            }}
          >
            {row.original.activity.isCriticalPath && (
              <Box
                width="3px"
                height="16px"
                borderRadius="4px"
                backgroundColor="red500"
              />
            )}
            <Box gridColumn="2">{formatUniqueId(value)}</Box>
          </Box>
        ),
      },
      {
        id: 'fileActivityId',
        accessor: 'activity.fileActivityId',
        width: 75,
        minWidth: 75,
        label: t('COLUMNS.FILE_ACTIVITY_ID'),
        renderCell: ({ value }: { value: string }) => (
          <OverflowTooltip>{value}</OverflowTooltip>
        ),
        renderHeader: () => (
          <OverflowTooltip>{t('COLUMNS.FILE_ACTIVITY_ID')}</OverflowTooltip>
        ),
      },
      {
        id: 'name',
        accessor: 'activity.name',
        width: 215,
        renderHeader: () => (
          <OverflowTooltip>{t('COLUMNS.ACTIVITY_NAME_COLUMN')}</OverflowTooltip>
        ),
        renderCell: ({
          row,
        }: {
          row: {
            depth: number;
            original: ActivityRow;
            canExpand: boolean;
            isExpanded: boolean;
            isSelected: boolean;
            getToggleRowExpandedProps: (arg0: { style?: CSSProperties }) => {
              style?: CSSProp | undefined;
            };
          };
        }) => (
          <Box
            display="grid"
            gridTemplateColumns="20px 1fr"
            alignItems="center"
            marginLeft={row.depth * 20}
            style={{ columnGap: '8px' }}
          >
            {row.canExpand &&
              (() => {
                const expandProps = row.getToggleRowExpandedProps({
                  style: {},
                });
                return (
                  <TableExpanderButton
                    {...expandProps}
                    style={expandProps.style}
                    isExpanded={row.original.expanded}
                    onClick={() => {
                      onExpandRow(row.original.id, !row.original.expanded);
                    }}
                  />
                );
              })()}
            <ActivityNameCell
              canExpand={row.canExpand}
              activity={row.original.activity}
              onClick={() =>
                toggleRowSelected(row.original.id, !row.isSelected)
              }
            />
          </Box>
        ),
      },
      {
        id: 'startDate',
        label: t('COLUMNS.START'),
        renderHeader: () => t('COLUMNS.START'),
        width: 90,
        accessor: 'activity.startDateToDisplay',
        renderCell: ({ row }: { row: { original: ActivityRow } }) => (
          <OverflowTooltip>
            {dateFormatter({
              date: row.original.activity.startDateToDisplay,
              isActual: !!row.original.activity.actualStart,
            })}
          </OverflowTooltip>
        ),
      },
      {
        id: 'endDate',
        accessor: 'activity.endDateToDisplay',
        label: t('COLUMNS.FINISH'),
        renderHeader: () => (
          <OverflowTooltip>{t('COLUMNS.FINISH')}</OverflowTooltip>
        ),
        width: 90,
        renderCell: ({ row }: { row: { original: ActivityRow } }) => (
          <OverflowTooltip>
            {dateFormatter({
              date: row.original.activity.endDateToDisplay,
              isActual: !!row.original.activity.actualFinish,
            })}
          </OverflowTooltip>
        ),
      },
      {
        id: 'progress',
        label: t('COLUMNS.PROGRESS'),
        renderHeader: () => (
          <OverflowTooltip>{t('COLUMNS.PROGRESS')}</OverflowTooltip>
        ),
        width: 90,
        accessor: 'activity.completionPercentage',
        renderCell: ({ value }: { value: number }) => (
          <OverflowTooltip>{`${formatFloatProgress(value)}%`}</OverflowTooltip>
        ),
      },
      {
        id: 'duration',
        label: t('COLUMNS.DURATION'),
        renderHeader: () => (
          <OverflowTooltip>{t('COLUMNS.DURATION')}</OverflowTooltip>
        ),
        width: 90,
        accessor: 'activity.durationDays',
        renderCell: ({ value }: { value: number | undefined }) => (
          <DurationCell value={value} />
        ),
      },
      {
        id: 'remainingDuration',
        label: t('COLUMNS.REMAINING_DURATION'),
        renderHeader: () => (
          <OverflowTooltip>{t('COLUMNS.REMAINING_DURATION')}</OverflowTooltip>
        ),
        width: 135,
        accessor: 'activity.remainingDurationDays',
        renderCell: ({ value }: { value: number | undefined }) => (
          <DurationCell value={value} />
        ),
      },
      {
        id: 'resources',
        label: t('COLUMNS.RESOURCES'),
        renderHeader: () => (
          <OverflowTooltip>{t('COLUMNS.RESOURCES')}</OverflowTooltip>
        ),
        width: 150,
        accessor: 'activity.assignees',
        renderCell: ({ value }: { value: { name: string }[] }) => (
          <OverflowTooltip>
            {value.length > 0
              ? value.map(({ name }: { name: string }) => name).join(', ')
              : '-'}
          </OverflowTooltip>
        ),
      },
    ],
    [t, onExpandRow, toggleRowSelected],
  );

  const settingsColumn = useMemo(
    () => ({
      id: 'settings',
      sticky: STICKY_TYPES.RIGHT,
      width: 20,
      renderHeader: (table: TableConfigurationMenuProps<ActivityRow>) => (
        <TableConfigurationMenu
          {...table}
          renderMenuContent={({
            handleToggleColumn,
            isColumnReorderable,
            handleOnDragEnd,
            validColumns,
            resetLabel,
            reset,
          }) => (
            <div style={{ minWidth: 280, paddingTop: '8px' }}>
              <TableConfigurationColumns
                handleToggleColumn={handleToggleColumn}
                handleOnDragEnd={handleOnDragEnd}
                validColumns={validColumns}
                isColumnReorderable={isColumnReorderable}
              />
              <Divider color={theme.colors.charcoal200} />

              <TableConfigurationReset label={resetLabel} reset={reset} />
            </div>
          )}
        />
      ),
    }),
    [],
  );

  return [...baseColumns, ...activityCodeColumns, settingsColumn];
};
